import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { useMediaQuery } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { Button } from '@sohanscript/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@sohanscript/ui/dialog';
import { SrOnly } from '@sohanscript/ui/sr-only';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { TransformWrapper } from 'react-zoom-pan-pinch';
import { SURFACE_EXIT_MS, SURFACE_MINIMIZE_MS } from '../../surface-frame';
import { useImageInspector } from '../../use-image-inspector';
import { WindowChrome } from '../../window-chrome';
import { useSurfaceDock } from '../surface-dock';
import type { ImageInspectorAsset } from './image-inspector-asset';
import {
	type InspectorStatus,
	SIDEBAR_MIN_SURFACE_WIDTH,
} from './image-inspector-shared';
import {
	ExposureSection,
	GpsSection,
	PaletteSection,
	SpecsSection,
	StatusStrip,
} from './image-inspector-sidebar';
import { SnippetsSection } from './image-inspector-snippets';
import {
	ImageStage,
	ImageStageSkeleton,
	InspectorBottomLine,
	ZoomControls,
} from './image-inspector-stage';

type ImageInspectorDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	asset: ImageInspectorAsset | null;
};

/** Tracks whether the surface is at least `minWidth` CSS pixels wide. */
function useSurfaceIsWide(minWidth: number) {
	const ref = useRef<HTMLDivElement>(null);
	const [isWide, setIsWide] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;
		const observer = new ResizeObserver(() => {
			// NOTE: a same-value set is a no-op, so dragging a resize handle
			// only re-renders when the surface crosses the threshold.
			setIsWide(element.clientWidth >= minWidth);
		});
		observer.observe(element);
		return () => observer.disconnect();
	}, [minWidth]);

	return [ref, isWide] as const;
}

function InspectorWorkspace({
	asset,
	onClose,
	onMinimize,
	isFullscreen,
	onToggleFullscreen,
}: {
	asset: ImageInspectorAsset;
	onClose: () => void;
	onMinimize: () => void;
	isFullscreen: boolean;
	onToggleFullscreen: () => void;
}) {
	const [surfaceRef, hasWideSurface] = useSurfaceIsWide(
		SIDEBAR_MIN_SURFACE_WIDTH,
	);
	const hasLgViewport = useMediaQuery('lg');
	const hasRoomForSidebar = hasLgViewport && hasWideSurface;
	const [isInspectorOpen, setIsInspectorOpen] = useState(false);
	const hasSidebarChoiceRef = useRef(false);
	const [fitScale, setFitScale] = useState(1);
	const { data, isLoading } = useImageInspector(asset.url, {
		filename: asset.filename,
		size: asset.size,
		mimeType: asset.mimeType,
		width: asset.width,
		height: asset.height,
	});

	const toggleSidebar = useCallback(() => {
		hasSidebarChoiceRef.current = true;
		setIsInspectorOpen((current) => !current);
	}, []);

	useEffect(() => {
		// NOTE: the sidebar opens by default only when the surface itself
		// leaves the stage enough room for the image to read large and clear.
		if (hasSidebarChoiceRef.current) return;
		setIsInspectorOpen(hasRoomForSidebar);
	}, [hasRoomForSidebar]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey || event.ctrlKey || event.altKey) return;
			const key = event.key.toLowerCase();
			if (key !== 'i' && key !== 'f' && key !== 'q') return;
			const target = event.target;
			if (
				target instanceof HTMLElement &&
				(target.isContentEditable ||
					['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
			) {
				return;
			}
			event.preventDefault();
			if (key === 'i') {
				toggleSidebar();
			} else if (key === 'f') {
				onToggleFullscreen();
			} else {
				onClose();
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [onClose, onToggleFullscreen, toggleSidebar]);

	const status: InspectorStatus = isLoading
		? 'reading'
		: data.isLimited
			? 'partial'
			: 'ready';

	const zoomControls = (
		<ZoomControls fitScale={fitScale} onFitScale={setFitScale} />
	);

	return (
		<div
			ref={surfaceRef}
			className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground"
		>
			<WindowChrome
				label={
					<div className="flex min-w-0 items-center justify-center gap-2">
						<span className="font-mono text-2xs text-primary uppercase tracking-widest">
							{m.image_inspector_label()}
						</span>
						<span className="text-primary/30">/</span>
						<DialogTitle className="truncate font-mono text-2xs font-normal text-muted-foreground normal-case tracking-normal">
							{data.filename}
						</DialogTitle>
					</div>
				}
				onClose={onClose}
				onMinimize={onMinimize}
				onMaximize={onToggleFullscreen}
				isMaximized={isFullscreen}
				className="bg-transparent"
				actions={
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						onClick={toggleSidebar}
						aria-label={m.image_inspector_toggle_aria()}
						aria-pressed={isInspectorOpen}
						aria-keyshortcuts="i"
						tooltip={m.image_inspector_toggle_title()}
						className="lg:translate-x-3 hover:translate-y-0  lg:hover:translate-x-2"
					>
						<Iconify
							icon="lucide:panel-right"
							className="size-4 max-lg:rotate-90"
							aria-hidden="true"
						/>
					</Button>
				}
			/>

			<DialogDescription>
				<SrOnly>
					{m.image_inspector_description({ filename: data.filename })}
				</SrOnly>
			</DialogDescription>

			<div className="flex min-h-0 flex-1 flex-col lg:flex-row">
				<TransformWrapper
					minScale={0.005}
					maxScale={40}
					fitOnInit="contain"
					centerZoomedOut
					limitToBounds
					wheel={{ disabled: true }}
					velocityAnimation={{ inertia: 0.1 }}
					doubleClick={{ mode: 'reset' }}
				>
					<div className="flex min-h-0 flex-1 flex-col">
						{isLoading ? (
							<ImageStageSkeleton width={data.width} height={data.height} />
						) : (
							<ImageStage
								url={asset.url}
								alt={data.filename}
								width={data.width}
								height={data.height}
							/>
						)}

						<InspectorBottomLine zoomControls={zoomControls} />
					</div>
				</TransformWrapper>

				<aside
					className={cn(
						'flex min-h-0 shrink-0 flex-col overflow-hidden border-border/60 bg-background-light transition-[height,width,opacity,border-top-width,border-left-width] duration-300 ease-out motion-reduce:transition-none',
						isInspectorOpen
							? 'h-[52dvh] border-t opacity-100 lg:h-auto lg:w-100 lg:border-t-0 lg:border-l'
							: 'h-0 opacity-0 lg:h-auto lg:w-0 lg:border-l-0',
					)}
					aria-label={m.image_inspector_aside_aria()}
					inert={!isInspectorOpen}
				>
					<div
						className={cn(
							'flex min-h-0 flex-1 flex-col transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none',
							isInspectorOpen
								? 'translate-x-0 translate-y-0 opacity-100'
								: 'translate-x-0 translate-y-2 opacity-0 lg:translate-x-2 lg:translate-y-0',
						)}
					>
						<StatusStrip status={status} format={data.format} />
						<div className="flex-1 overflow-y-auto overscroll-contain scrollbar-thin">
							<PaletteSection palette={data.palette} isLoading={isLoading} />
							<SpecsSection data={data} />
							{data.exif && <ExposureSection exif={data.exif} />}
							{data.gps && <GpsSection gps={data.gps} />}
							<SnippetsSection data={data} url={asset.url} />
						</div>
					</div>
				</aside>
			</div>
		</div>
	);
}

export function ImageInspectorDialog({
	open,
	onOpenChange,
	asset,
}: ImageInspectorDialogProps) {
	const [lastAsset, setLastAsset] = useState(asset);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isVisible, setIsVisible] = useState(open);
	const [isClosing, setIsClosing] = useState(false);
	const [isMinimized, setIsMinimized] = useState(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const minimizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const dockId = useId();
	const { register, unregister } = useSurfaceDock();
	const displayAsset = asset ?? lastAsset;

	useEffect(() => {
		if (asset) setLastAsset(asset);
	}, [asset]);

	useEffect(() => {
		if (open) {
			if (closeTimer.current) {
				clearTimeout(closeTimer.current);
				closeTimer.current = null;
			}
			if (minimizeTimer.current) {
				clearTimeout(minimizeTimer.current);
				minimizeTimer.current = null;
			}
			setIsMinimized(false);
			setIsVisible(true);
			setIsClosing(false);
		}
	}, [open]);

	useEffect(
		() => () => {
			if (closeTimer.current) clearTimeout(closeTimer.current);
			if (minimizeTimer.current) clearTimeout(minimizeTimer.current);
		},
		[],
	);

	const toggleFullscreen = useCallback(() => {
		setIsFullscreen((current) => !current);
	}, []);

	const handleOpenChange = useCallback(
		(nextOpen: boolean, details?: { reason?: string }) => {
			if (nextOpen) {
				if (closeTimer.current) {
					clearTimeout(closeTimer.current);
					closeTimer.current = null;
				}
				setIsVisible(true);
				setIsClosing(false);
				onOpenChange(true);
				return;
			}
			// NOTE: a window keeps its place: clicking the page behind it
			// must not close it, and a drag ends outside the frame.
			if (details?.reason === 'outside-press') return;
			if (isClosing) return;

			// NOTE: closing from the dock or the keyboard drops the entry,
			// only a minimize leaves the window parked in the dock.
			if (minimizeTimer.current) {
				clearTimeout(minimizeTimer.current);
				minimizeTimer.current = null;
			}
			setIsMinimized(false);

			// NOTE: the frame animates the surface out, so the popup stays
			// mounted for the length of the transition. The closing state
			// stays put until the surface opens again, otherwise the frame
			// would animate back in while it is on its way out.
			setIsClosing(true);
			closeTimer.current = setTimeout(() => {
				closeTimer.current = null;
				setIsVisible(false);
				onOpenChange(false);
			}, SURFACE_EXIT_MS);
		},
		[isClosing, onOpenChange],
	);

	const minimize = useCallback(() => {
		if (isMinimized) return;

		setIsMinimized(true);
		// NOTE: the surface folds out of the page, then unmounts so the
		// dialog stops trapping focus while it sits in the dock.
		minimizeTimer.current = setTimeout(() => {
			minimizeTimer.current = null;
			setIsVisible(false);
			onOpenChange(false);
		}, SURFACE_MINIMIZE_MS);
	}, [isMinimized, onOpenChange]);

	const restore = useCallback(() => {
		if (minimizeTimer.current) {
			clearTimeout(minimizeTimer.current);
			minimizeTimer.current = null;
		}
		setIsMinimized(false);
		setIsClosing(false);
		setIsVisible(true);
		onOpenChange(true);
	}, [onOpenChange]);

	const dismiss = useCallback(() => {
		if (minimizeTimer.current) {
			clearTimeout(minimizeTimer.current);
			minimizeTimer.current = null;
		}
		setIsMinimized(false);
		setIsVisible(false);
		onOpenChange(false);
	}, [onOpenChange]);

	useEffect(() => {
		if (!isMinimized) return;

		const title =
			displayAsset?.filename ??
			displayAsset?.url.split('/').pop() ??
			m.image_inspector_label();

		register({
			id: dockId,
			title,
			caption: m.image_inspector_label(),
			icon: 'lucide:image',
			restore,
			close: dismiss,
		});

		return () => unregister(dockId);
	}, [
		isMinimized,
		dockId,
		displayAsset?.filename,
		displayAsset?.url,
		register,
		unregister,
		restore,
		dismiss,
	]);

	return (
		<Dialog open={isVisible} onOpenChange={handleOpenChange}>
			<DialogContent
				showCloseButton={false}
				surface="window"
				maximized={isFullscreen}
				maximizedMode="cover"
				draggable
				state={isMinimized ? 'minimized' : isClosing ? 'closed' : 'open'}
				className="h-[min(46rem,80dvh)] w-[min(68rem,78vw)]"
			>
				{displayAsset && (
					<InspectorWorkspace
						key={displayAsset.url}
						asset={displayAsset}
						onClose={() => handleOpenChange(false)}
						onMinimize={minimize}
						isFullscreen={isFullscreen}
						onToggleFullscreen={toggleFullscreen}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}
